import { FC, ReactNode } from "react";
import { Box, Typography } from "@mui/material";
import {
  IConversationAmount,
  IConversationPurchase,
} from "@/store/api/conversationsApiData";
import { ZapBadgeIcon, LikeBadgeIcon, PurchaseBadgeIcon } from "./ConversationBadgeIcons";
import { formatMoney } from "@/helpers/formatMoney";

interface EngagementBadgesProps {
  like?: IConversationAmount | null;
  zap?: IConversationAmount | null;
  purchase?: IConversationPurchase | null;
  prefix?: string;
  variant?: "pill" | "plain";
}

const PillBadge: FC<{
  icon: ReactNode;
  amount: number;
  bg: string;
  border: string;
  textColor: string;
  prefix: string;
}> = ({ icon, amount, bg, border, textColor, prefix }) => (
  <Box
    sx={{
      display: "flex",
      alignItems: "center",
      gap: "2px",
      pl: "4px",
      pr: "8px",
      py: "2px",
      borderRadius: "20px",
      backgroundColor: bg,
      border: `1px solid ${border}`,
    }}
  >
    {icon}
    <Typography
      sx={{
        fontFamily: "Inter",
        fontWeight: 600,
        fontSize: "10px",
        lineHeight: "16px",
        color: textColor,
        whiteSpace: "nowrap",
      }}
    >
      {prefix}
      {formatMoney(amount)} USDC
    </Typography>
  </Box>
);

const PlainBadge: FC<{ icon: ReactNode; amount: number }> = ({ icon, amount }) => (
  <Box sx={{ display: "flex", alignItems: "center", gap: "2px" }}>
    {icon}
    <Typography
      sx={{
        fontFamily: "Inter",
        fontWeight: 500,
        fontSize: "12px",
        lineHeight: "16px",
        color: "#000000",
        whiteSpace: "nowrap",
      }}
    >
      {formatMoney(amount)} USDC
    </Typography>
  </Box>
);

const EngagementBadges: FC<EngagementBadgesProps> = ({
  like,
  zap,
  purchase,
  prefix = "+",
  variant = "pill",
}) => {
  const hasZap = !!zap && zap.netAmount > 0;
  const hasLike = !!like && like.netAmount > 0;
  const hasPurchase = !!purchase && purchase.amount > 0;

  if (!hasZap && !hasLike && !hasPurchase) return null;

  if (variant === "plain") {
    return (
      <Box sx={{ display: "flex", alignItems: "center", gap: "6px" }}>
        {hasLike && <PlainBadge icon={<LikeBadgeIcon size={18} />} amount={like!.netAmount} />}
        {hasZap && <PlainBadge icon={<ZapBadgeIcon size={18} />} amount={zap!.netAmount} />}
      </Box>
    );
  }

  return (
    <>
      {hasZap && (
        <PillBadge
          icon={<ZapBadgeIcon size={16} />}
          amount={zap!.netAmount}
          bg="#e8f5e8"
          border="rgba(0,128,1,0.05)"
          textColor="#16a34a"
          prefix={prefix}
        />
      )}
      {hasLike && (
        <PillBadge
          icon={<LikeBadgeIcon size={16} />}
          amount={like!.netAmount}
          bg="#e8f5e8"
          border="rgba(0,128,1,0.05)"
          textColor="#16a34a"
          prefix={prefix}
        />
      )}
      {hasPurchase && (
        <PillBadge
          icon={<PurchaseBadgeIcon size={16} />}
          amount={purchase!.amount}
          bg="#e1f6ff"
          border="rgba(44,116,146,0.05)"
          textColor="#0052b4"
          prefix={prefix}
        />
      )}
    </>
  );
};

export default EngagementBadges;
